import React, { useState, useEffect, useRef } from 'react';
import {
  getInventoryParts, addLaborToWorkOrder, editLaborOnWorkOrder, removeLaborFromWorkOrder,
  getActiveTimer, startLaborTimer, stopLaborTimer, cancelLaborTimer,
  resolvePendingLabourRate,
  detachOrphanInvoice, reattachWorkOrderToInvoice, getInvoices,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Clock, X, Receipt, Play, Square, Sparkles, Pencil, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Labour tracking panel. Two paths:
 *   - **Manual**: pick rate, type hours, click Add (the original flow).
 *   - **Timer**:  pick rate, click Start. While running, the panel shows a
 *     ticking clock and a Stop button — Stop logs the elapsed time as a
 *     labour entry on the WO. Useful for engineers who want exact hours
 *     without doing mental arithmetic mid-job.
 *
 * Active timer state lives on the server (collection `labor_timers`) so a
 * phone refresh / app re-open still resumes the running clock — critical
 * for the on-site mobile use case.
 *
 * **Privacy mode (engineers)**: the rate matrix is admin-controlled (rate
 * cards by employee × customer × time-of-day). Engineers never see £
 * amounts — no rate picker, no totals, no per-hour figure on the timer.
 * They just enter hours (or run the timer) and submit; the backend's
 * rate-card engine resolves the rate server-side.
 */
export const WorkOrderLaborPanel = ({ workOrder, onChange }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEngineer = user?.role === 'employee' || user?.role === 'subcontractor';
  const isCustomer = user?.role === 'customer';
  // Labour panel mutations and the new reattach/detach orphan flow both
  // call the same parent refresh callback so the dialog repaints with
  // the latest WO state.
  const onChanged = onChange;

  const [labor, setLabor] = useState(workOrder.labor || []);
  const [rates, setRates] = useState([]);
  const [rateId, setRateId] = useState('');
  const [hours, setHours] = useState(1);
  const [description, setDescription] = useState('');
  // Optional back-fill date — defaults to today. When the engineer picks
  // a different date, the backend stamps the labor row at noon UTC on
  // that date so it lands cleanly inside the day summary for that date.
  const [logDate, setLogDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);
  // Active timer = { id, rateId, rateName, rate, startedAt, ... } or null.
  // For engineers, rate fields are scrubbed by the backend.
  const [activeTimer, setActiveTimer] = useState(null);
  // `nowMs` updates every second while timer is running so the displayed
  // elapsed clock ticks. Stored on state (not Date.now() inline) so the
  // component re-renders on each tick.
  const [nowMs, setNowMs] = useState(Date.now());
  const tickRef = useRef(null);

  useEffect(() => { setLabor(workOrder.labor || []); }, [workOrder.labor]);

  useEffect(() => {
    // Engineers don't need the rate list — backend resolves rate server-side.
    if (!isEngineer) {
      getInventoryParts()
        .then(all => setRates(all.filter(p =>
          !p.isDraft && (p.category === 'Labor' || p.xeroIsTracked === false)
        )))
        .catch(() => {});
    }
    // Resume any in-flight timer for this user on this WO.
    getActiveTimer(workOrder.id)
      .then(t => {
        if (t && t.id) {
          setActiveTimer(t);
          setRateId(t.rateId || '');
          setDescription(t.description || '');
        }
      })
      .catch(() => {});
  }, [workOrder.id, isEngineer]);

  // Live tick — only mounted while a timer is running so we don't waste
  // re-renders on idle work-order pages.
  useEffect(() => {
    if (!activeTimer) {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      return;
    }
    setNowMs(Date.now());
    tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [activeTimer]);

  const selectedRate = rates.find(r => r.id === rateId);
  const displayRate = selectedRate ? (selectedRate.sellPrice ?? selectedRate.unitCost ?? 0) : 0;
  const lineTotal = (Number(hours) || 0) * displayRate;

  // Live timer values
  const elapsedSec = activeTimer
    ? Math.max(0, Math.floor((nowMs - new Date(activeTimer.startedAt).getTime()) / 1000))
    : 0;
  const elapsedHrs = elapsedSec / 3600;
  const elapsedDisplay = formatHMS(elapsedSec);
  const liveLineTotal = elapsedHrs * (Number(activeTimer?.rate) || 0);

  const add = async () => {
    // Engineers don't choose a rate — backend resolves via rate card.
    if (!isEngineer && !rateId) return toast.error('Pick a labour rate');
    if (!hours || Number(hours) <= 0) return toast.error('Enter hours > 0');
    // Only forward `date` when the engineer picked something other than
    // today — cuts request noise and lets the backend keep its "now"
    // fast-path when nothing was overridden.
    const todayIso = new Date().toISOString().slice(0, 10);
    const dateField = logDate && logDate !== todayIso ? { date: logDate } : {};
    setSubmitting(true);
    try {
      const payload = isEngineer
        ? { hours: Number(hours), description, ...dateField }
        : { partId: rateId, hours: Number(hours), description, ...dateField };
      const entry = await addLaborToWorkOrder(workOrder.id, payload);
      setLabor([...labor, entry]);
      setHours(1);
      setDescription('');
      setLogDate(todayIso); // reset back to today after a successful back-fill
      if (onChange) onChange();
      // Engineer toast: hours only. Admin toast: keep £ readout.
      const dateSuffix = dateField.date ? ` for ${dateField.date}` : '';
      if (isEngineer) {
        toast.success(`Logged ${entry.hours}h${dateSuffix}`);
      } else {
        toast.success(`Added ${entry.hours}h @ £${(entry.rate || 0).toFixed(2)}/h${dateSuffix}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add labour');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (i) => {
    if (!window.confirm('Remove this labour entry?')) return;
    try {
      await removeLaborFromWorkOrder(workOrder.id, i);
      setLabor(labor.filter((_, idx) => idx !== i));
      if (onChange) onChange();
    } catch { toast.error('Remove failed'); }
  };

  // -------- Inline edit -------------------------------------------------
  // `editingIndex` opens an inline form ON the row (hours + notes + date).
  // Kept lightweight (no modal) so an engineer can fix a slip in a
  // couple of taps on a phone. Backend gate-keeps who can edit what:
  // engineers may edit only their own rows; already-invoiced rows are
  // 409'd server-side.
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editForm, setEditForm] = useState({ hours: '', description: '', date: '' });
  const [editSaving, setEditSaving] = useState(false);

  const beginEdit = (i) => {
    const row = labor[i];
    setEditingIndex(i);
    setEditForm({
      hours: String(row.hours ?? ''),
      description: row.description || '',
      date: (row.addedAt || '').slice(0, 10),
    });
  };
  const cancelEdit = () => { setEditingIndex(-1); };
  const saveEdit = async (i) => {
    const original = labor[i];
    const patch = {};
    const h = Number(editForm.hours);
    if (!Number.isFinite(h) || h <= 0) return toast.error('Enter hours > 0');
    if (h !== original.hours) patch.hours = h;
    if ((editForm.description || '') !== (original.description || '')) patch.description = editForm.description;
    const origDate = (original.addedAt || '').slice(0, 10);
    if (editForm.date && editForm.date !== origDate) patch.date = editForm.date;
    if (Object.keys(patch).length === 0) { setEditingIndex(-1); return; }
    setEditSaving(true);
    try {
      const updated = await editLaborOnWorkOrder(workOrder.id, i, patch);
      setLabor(list => list.map((e, idx) => (idx === i ? { ...e, ...updated } : e)));
      setEditingIndex(-1);
      if (onChange) onChange();
      toast.success('Updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };
  const canEdit = (row) => {
    if (row.billedInvoiceId) return false;
    if (isAdmin) return true;
    if (isEngineer && row.addedById === user?.id) return true;
    return false;
  };

  // Inline "Auto-resolve" on a `needs rate` labour row. Re-runs the
  // rate-card matcher for this entry's engineer + WO customer + entry
  // timestamp, so admins can fix rates right where they see the problem
  // without having to wait for the WO to hit admin-review state.
  const autoResolveRate = async (i) => {
    try {
      const updated = await resolvePendingLabourRate(workOrder.id, i, { autoResolve: true });
      setLabor(list => list.map((e, idx) => (idx === i ? { ...e, ...updated } : e)));
      toast.success(`Rate resolved · £${Number(updated.rate || 0).toFixed(2)}/h`);
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-resolve failed');
    }
  };

  const startTimer = async () => {
    // Admins must pick a rate; engineers leave it blank for the rate-card engine.
    if (!isEngineer && !rateId) return toast.error('Pick a labour rate first');
    setSubmitting(true);
    try {
      const payload = isEngineer
        ? { description }
        : { rateId, description };
      const t = await startLaborTimer(workOrder.id, payload);
      setActiveTimer(t);
      toast.success('Timer started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start timer');
    } finally { setSubmitting(false); }
  };

  const stopTimer = async () => {
    setSubmitting(true);
    try {
      const entry = await stopLaborTimer(workOrder.id);
      setActiveTimer(null);
      setLabor([...labor, entry]);
      if (onChange) onChange();
      // Engineer toast: hours only. Admin toast: keep £ readout.
      if (isEngineer) {
        toast.success(`Logged ${entry.hours}h`);
      } else {
        toast.success(`Logged ${entry.hours}h · £${(entry.lineTotal || 0).toFixed(2)}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to stop timer');
    } finally { setSubmitting(false); }
  };

  const cancelTimer = async () => {
    if (!window.confirm('Discard the running timer? Nothing will be logged.')) return;
    try {
      await cancelLaborTimer(workOrder.id);
      setActiveTimer(null);
      toast.success('Timer discarded');
    } catch { toast.error('Cancel failed'); }
  };

  const totalHours = labor.reduce((a, l) => a + (l.hours || 0), 0);
  const totalCost = labor.reduce((a, l) => a + (l.lineTotal || 0), 0);

  return (
    <div className="pt-4 border-t border-slate-200">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-slate-500">Labour time</div>
        {labor.length > 0 && (
          <div className="text-xs text-slate-600">
            {totalHours.toFixed(1)}h
            {!isEngineer && <> · <span className="font-semibold text-slate-900">£{totalCost.toFixed(2)}</span></>}
          </div>
        )}
      </div>

      {/* Active timer banner — only shown while running. Live-ticking
          elapsed clock + a Stop CTA. The dark green chrome keeps it
          visually distinct from the normal entry list. */}
      {activeTimer && (
        <div
          className="bg-emerald-700 text-white rounded-md p-3 mb-3 flex items-center gap-3"
          data-testid="active-timer-banner"
        >
          <div className="h-9 w-9 rounded-full bg-emerald-900/40 flex items-center justify-center shrink-0">
            <Clock className="h-4 w-4 animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-emerald-200">
              {isEngineer ? 'Timer running' : `Timer running · ${activeTimer.rateName || 'Labour'}`}
            </div>
            <div className="font-display text-xl tabular-nums leading-tight">{elapsedDisplay}</div>
            {/* Engineers don't see £ — admins see live cost so they can manage WIP. */}
            {!isEngineer && (
              <div className="text-[11px] text-emerald-100">
                £{(Number(activeTimer.rate) || 0).toFixed(2)}/h ·{' '}
                <span className="font-semibold">£{liveLineTotal.toFixed(2)} so far</span>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <Button
              size="sm"
              onClick={stopTimer}
              disabled={submitting}
              className="bg-white text-emerald-900 hover:bg-emerald-50 h-8"
              data-testid="stop-timer-btn"
            >
              <Square className="h-3.5 w-3.5 mr-1" /> Stop
            </Button>
            <button
              type="button"
              onClick={cancelTimer}
              className="text-[10px] text-emerald-200 hover:text-white underline"
              data-testid="cancel-timer-btn"
            >
              cancel
            </button>
          </div>
        </div>
      )}

      {labor.length === 0 && !activeTimer && (
        <div className="text-sm text-slate-500 mb-3">No labour logged yet.</div>
      )}
      <div className="space-y-1.5 mb-3">
        {labor.map((l, i) => (
          editingIndex === i ? (
            <div key={i} data-testid={`wo-labor-${i}-edit`} className="bg-white border border-emerald-300 rounded p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Editing entry</div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-xs">For</Label>
                  <Input
                    type="date"
                    value={editForm.date}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                    className="mt-1"
                    data-testid={`edit-labor-date-${i}`}
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-xs">Hours</Label>
                  <Input
                    type="number" step="0.25" min="0.25"
                    value={editForm.hours}
                    onChange={e => setEditForm(f => ({ ...f, hours: e.target.value }))}
                    className="mt-1"
                    data-testid={`edit-labor-hours-${i}`}
                  />
                </div>
                <div className="col-span-8 sm:col-span-6">
                  <Label className="text-xs">Notes</Label>
                  <Input
                    value={editForm.description}
                    onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                    className="mt-1"
                    data-testid={`edit-labor-notes-${i}`}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm" variant="outline"
                  onClick={cancelEdit}
                  disabled={editSaving}
                  data-testid={`edit-labor-cancel-${i}`}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => saveEdit(i)}
                  disabled={editSaving}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid={`edit-labor-save-${i}`}
                >
                  <Check className="h-3.5 w-3.5 mr-1" />
                  {editSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <div key={i} data-testid={`wo-labor-${i}`} className="flex items-center gap-3 text-sm bg-slate-50 border border-slate-200 rounded px-3 py-2">
              <Clock className="h-4 w-4 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{l.hours}h · {l.description}</div>
                <div className="text-xs text-slate-500 truncate">
                  {/* Admins see the full £ math; engineers see only attribution + flags. */}
                  {!isEngineer && <>£{(l.rate || 0).toFixed(2)}/h = £{(l.lineTotal || 0).toFixed(2)} · </>}
                  {l.addedBy}
                  {l.addedAt && ` · ${l.addedAt.slice(0, 10)}`}
                  {l.editedAt && <span className="ml-1 text-slate-400" title={`Edited ${l.editedAt.slice(0, 16)} by ${l.editedBy || ''}`}>· edited</span>}
                  {l.sourceTimerId && <span className="ml-1 text-emerald-700">· timed</span>}
                  {l.billedInvoiceId && <span className="ml-1 text-blue-700">· on invoice</span>}
                  {l.needsRateResolution && !isEngineer && (
                    <span className="ml-1 text-amber-700 font-semibold">· needs rate</span>
                  )}
                </div>
              </div>
              {l.needsRateResolution && isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs shrink-0"
                  onClick={() => autoResolveRate(i)}
                  data-testid={`wo-autoresolve-inline-${i}`}
                  title="Re-run rate-card matcher for this engineer + customer + time band"
                >
                  <Sparkles className="h-3 w-3 mr-1" /> Auto-resolve
                </Button>
              )}
              {canEdit(l) && (
                <Button
                  size="icon" variant="ghost" className="h-7 w-7 shrink-0"
                  onClick={() => beginEdit(i)}
                  data-testid={`edit-labor-${i}`}
                  title="Edit hours, notes or date"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {isAdmin && (
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => remove(i)} data-testid={`remove-labor-${i}`}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )
        ))}
      </div>

      {/* Add-labour form (hidden while timer is running — Stop is the only path).
          The form shape changes based on role: engineers get hours+notes only,
          admins keep the full rate picker. */}
      {!activeTimer && (isEngineer ? (
        <EngineerLaborForm
          hours={hours}
          setHours={setHours}
          description={description}
          setDescription={setDescription}
          logDate={logDate}
          setLogDate={setLogDate}
          submitting={submitting}
          onAdd={add}
          onStartTimer={startTimer}
        />
      ) : (rates.length === 0 ? (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          No legacy Labour parts found. Admins can log time by typing hours and clicking Add — the rate-card engine resolves the £/h automatically. To edit rates, go to <a href="/admin/rate-cards" className="underline font-medium">Rate Cards</a>.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-12 sm:col-span-4">
              <Label className="text-xs">Rate</Label>
              <Select value={rateId} onValueChange={setRateId}>
                <SelectTrigger className="mt-1" data-testid="labor-rate-select"><SelectValue placeholder="Select labour rate" /></SelectTrigger>
                <SelectContent>
                  {rates.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} — £{(r.sellPrice ?? r.unitCost ?? 0).toFixed(2)}/h
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-12 sm:col-span-2">
              <Label className="text-xs flex items-center gap-1">
                For
                {logDate && logDate !== new Date().toISOString().slice(0, 10) && (
                  <button
                    type="button"
                    onClick={() => setLogDate(new Date().toISOString().slice(0, 10))}
                    className="text-[10px] text-emerald-700 hover:underline"
                    data-testid="labor-date-reset-today"
                  >
                    today
                  </button>
                )}
              </Label>
              <Input
                type="date"
                value={logDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setLogDate(e.target.value)}
                className="mt-1"
                data-testid="labor-date-input"
              />
            </div>
            <div className="col-span-4 sm:col-span-2">
              <Label className="text-xs">Hours</Label>
              <Input type="number" step="0.25" min="0.25" value={hours} onChange={e => setHours(e.target.value)} className="mt-1" data-testid="labor-hours-input" />
            </div>
            <div className="col-span-8 sm:col-span-4">
              <Label className="text-xs">Notes</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button
              onClick={startTimer}
              disabled={submitting || !rateId}
              variant="outline"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid="start-timer-btn"
            >
              <Play className="h-3.5 w-3.5 mr-1.5" /> Start timer
            </Button>
            <Button
              onClick={add}
              disabled={submitting || !rateId}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-labor-btn"
            >
              Add manually {lineTotal > 0 && `· £${lineTotal.toFixed(2)}`}
            </Button>
          </div>
        </div>
      )))}

      {workOrder.invoiceId && !isEngineer && (
        <div
          className={`mt-3 rounded px-3 py-2 text-sm ${
            workOrder.invoiceMissing
              ? 'bg-rose-50 border border-rose-200 text-rose-900'
              : 'bg-blue-50 border border-blue-200 text-blue-900'
          }`}
          data-testid={`wo-invoice-banner-${workOrder.id}`}
        >
          <div className="flex items-start gap-2">
            <Receipt className="h-4 w-4 shrink-0 mt-0.5" />
            {workOrder.invoiceMissing ? (
              <div className="flex-1 space-y-1">
                <div>
                  This work order references{' '}
                  {workOrder.invoiceXeroNumber ? (
                    <>
                      Xero invoice{' '}
                      <span className="font-mono font-semibold">{workOrder.invoiceXeroNumber}</span>
                      {' '}(internal{' '}
                      <span className="font-mono">{workOrder.invoiceId}</span>)
                    </>
                  ) : (
                    <>
                      invoice{' '}
                      <span className="font-mono font-semibold">{workOrder.invoiceId}</span>
                    </>
                  )}{' '}
                  but it can't be found anymore (deleted, voided, or renamed in Xero).
                </div>
                {!isCustomer && (
                  <ReattachInvoiceActions
                    workOrder={workOrder}
                    onChanged={onChanged}
                  />
                )}
              </div>
            ) : (
              <span>
                This work order is on invoice{' '}
                <a
                  href={`/admin/invoices/${workOrder.invoiceId}`}
                  className="font-semibold underline"
                  data-testid={`wo-invoice-link-${workOrder.id}`}
                >
                  {workOrder.invoiceXeroNumber || workOrder.invoiceId}
                </a>
                {workOrder.invoiceXeroNumber && (
                  <span className="ml-1 text-xs text-blue-700/70">
                    (internal {workOrder.invoiceId})
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/** Hours-only form for engineers. No rate picker, no totals — backend
 *  resolves the rate via rate-card matrix at submission time.
 *
 *  Includes a "For" date input so an engineer can back-fill labour
 *  against a day they forgot to log — the backend stamps that row at
 *  noon UTC on the picked date. Defaults to today; a small "today"
 *  chip resets the picker in one tap. Future dates are blocked at the
 *  input level (`max=today`) with a defensive backend clamp behind it.
 */
const EngineerLaborForm = ({ hours, setHours, description, setDescription, logDate, setLogDate, submitting, onAdd, onStartTimer }) => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const isBackfill = logDate && logDate !== todayIso;
  return (
    <div className="space-y-2">
      <div className="text-[11px] text-slate-500 italic">
        Your hourly rate is set by your admin. Just log the time you worked.
      </div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 sm:col-span-3">
          <Label className="text-xs flex items-center gap-1">
            For
            {isBackfill && (
              <button
                type="button"
                onClick={() => setLogDate(todayIso)}
                className="text-[10px] text-emerald-700 hover:underline"
                data-testid="labor-date-reset-today"
              >
                today
              </button>
            )}
          </Label>
          <Input
            type="date"
            value={logDate}
            max={todayIso}
            onChange={e => setLogDate(e.target.value)}
            className="mt-1"
            data-testid="labor-date-input"
          />
        </div>
        <div className="col-span-4 sm:col-span-2">
          <Label className="text-xs">Hours</Label>
          <Input
            type="number" step="0.25" min="0.25"
            value={hours}
            onChange={e => setHours(e.target.value)}
            className="mt-1"
            data-testid="labor-hours-input"
          />
        </div>
        <div className="col-span-8 sm:col-span-7">
          <Label className="text-xs">Notes</Label>
          <Input
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What did you do?"
            className="mt-1"
            data-testid="labor-notes-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          onClick={onStartTimer}
          disabled={submitting || isBackfill}
          variant="outline"
          className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
          data-testid="start-timer-btn"
          title={isBackfill ? 'Timer is for logging live work — clear the date to enable' : undefined}
        >
          <Play className="h-3.5 w-3.5 mr-1.5" /> Start timer
        </Button>
        <Button
          onClick={onAdd}
          disabled={submitting}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-labor-btn"
        >
          Log hours{isBackfill && ` · ${logDate}`}
        </Button>
      </div>
    </div>
  );
};

/** Format seconds → "HH:MM:SS" with a leading zero per segment. Reusable
 *  inside JSX without importing date-fns. */
function formatHMS(totalSec) {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}



/** Inline actions surfaced on the orphan-invoice banner. Two paths:
 *
 * 1. **Detach** — clears the WO's `invoiceId` foreign key so the
 *    auto-accumulator can pick it back up onto a fresh invoice.
 * 2. **Reattach** — opens a tiny picker that lists every local
 *    invoice with a Xero number, lets the admin pick the renamed /
 *    correct one, and updates the WO's foreign key (no line items
 *    moved — purely a relink).
 *
 * Admin-only; the parent guards with `!isCustomer`. We refresh the
 * dialog via `onChanged()` after either action so the banner repaints
 * with the new state immediately.
 */
const ReattachInvoiceActions = ({ workOrder, onChanged }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const openPicker = async () => {
    setPickerOpen(true);
    if (invoices.length === 0) {
      setLoadingInvoices(true);
      try {
        const all = await getInvoices();
        // Show every invoice, sorted with Xero-numbered ones first so
        // the admin can easily spot the renamed one.
        const sorted = (all || []).slice().sort((a, b) => {
          const ax = a.xeroInvoiceNumber || '';
          const bx = b.xeroInvoiceNumber || '';
          if (ax && !bx) return -1;
          if (!ax && bx) return 1;
          return bx.localeCompare(ax);
        });
        setInvoices(sorted);
      } catch (e) {
        toast.error('Failed to load invoices');
      } finally {
        setLoadingInvoices(false);
      }
    }
  };

  const detachOrphan = async () => {
    if (!window.confirm(
      `Detach this work order from ${workOrder.invoiceXeroNumber || workOrder.invoiceId}?\n\n` +
      `It will then be picked up by the next auto-bill cycle so it can land on a fresh invoice.`,
    )) return;
    setBusy(true);
    try {
      await detachOrphanInvoice(workOrder.id);
      toast.success('Detached. The WO can now be re-billed.');
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Detach failed');
    } finally {
      setBusy(false);
    }
  };

  const reattach = async (invoiceId, xeroNumber) => {
    if (!window.confirm(
      `Re-attach this work order to ${xeroNumber || invoiceId}?\n\n` +
      `This only updates the link — no line items are moved across.`,
    )) return;
    setBusy(true);
    try {
      await reattachWorkOrderToInvoice(workOrder.id, invoiceId);
      toast.success(`Re-attached to ${xeroNumber || invoiceId}`);
      setPickerOpen(false);
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-attach failed');
    } finally {
      setBusy(false);
    }
  };

  const filtered = invoices.filter((inv) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (inv.xeroInvoiceNumber || '').toLowerCase().includes(q) ||
      (inv.id || '').toLowerCase().includes(q) ||
      (inv.customer || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex items-center gap-2 pt-1">
      <button
        type="button"
        onClick={openPicker}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded border border-rose-400 bg-white px-2 py-1 text-xs font-medium text-rose-800 hover:bg-rose-100 disabled:opacity-60"
        data-testid={`wo-reattach-btn-${workOrder.id}`}
      >
        Re-attach to a different invoice
      </button>
      <button
        type="button"
        onClick={detachOrphan}
        disabled={busy}
        className="inline-flex items-center gap-1 rounded border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
        data-testid={`wo-detach-orphan-btn-${workOrder.id}`}
      >
        Detach (let auto-bill re-pick)
      </button>

      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 p-4"
          onClick={() => !busy && setPickerOpen(false)}
          data-testid={`wo-reattach-picker-${workOrder.id}`}
        >
          <div
            className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-lg w-full mt-12 max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Pick the correct invoice</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Re-attach {workOrder.id} to a different local invoice. Search by Xero
                  number, internal ID, or customer.
                </p>
              </div>
              <button
                onClick={() => setPickerOpen(false)}
                className="text-slate-500 hover:text-slate-800"
                data-testid="wo-reattach-picker-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-slate-200">
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search… (e.g. IN202600024)"
                autoFocus
                data-testid="wo-reattach-picker-search"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingInvoices ? (
                <div className="p-4 text-sm text-slate-500">Loading invoices…</div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-sm text-slate-500">No invoices match your search.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filtered.slice(0, 200).map((inv) => (
                    <li key={inv.id} className="flex items-center justify-between px-4 py-2 hover:bg-slate-50">
                      <div className="min-w-0 mr-3">
                        <div className="font-mono text-sm font-semibold text-slate-900 truncate">
                          {inv.xeroInvoiceNumber || inv.id}
                          {inv.xeroInvoiceNumber && (
                            <span className="ml-1 text-xs font-sans text-slate-500">
                              (internal {inv.id})
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {inv.customer || '—'} · £{Number(inv.amount || 0).toFixed(2)} · {inv.status}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy || inv.id === workOrder.invoiceId}
                        onClick={() => reattach(inv.id, inv.xeroInvoiceNumber)}
                        data-testid={`wo-reattach-pick-${inv.id}`}
                      >
                        Re-attach
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
